import { UnitConversion } from '../inputs/UnitConversion';
import ValidateProductValue from './ValidateProductValue';
import ValidateValue from './ValidateValue';

export function checkProductReady(prod, productFlow) {
  return (
    ((prod['product_shape'] === 'rectangular' &&
      prod['length'] > 0 &&
      prod['width'] > 0) ||
      (prod['product_shape'] === 'round' && prod['diameter'] > 0)) &&
    prod['height'] > 0 &&
    (productFlow === 'mass flow' || prod['throughput'] > 0)
  );
}

export function checkProductState(
  product_tbh,
  pdp_list,
  result,
  svFamily,
  productFlow
) {
  const isNum = (v) => Number.isFinite(v);
  const tooSmall = (v) => isNum(v) && v <= 0;

  const baseProductState = product_tbh.map((product) => {
    const sizePresent =
      ((product['product_shape'] === 'rectangular' &&
        product['length'] > 0 &&
        product['width'] > 0) ||
        (product['product_shape'] === 'round' && product['diameter'] > 0)) &&
      product['height'] > 0;
    const flowPresent =
      product['weight'] > 0 &&
      (productFlow === 'mass flow' || product['throughput'] > 0);

    const selectedPdp = product.pdp
      ? pdp_list.find((item) => item.id === product.pdp)
      : null;

    const sizeToCheck =
      product['product_shape'] === 'round'
        ? ['diameter', 'height']
        : ['length', 'width', 'height'];

    const flowToCheck =
      productFlow === 'mass flow' ? ['weight'] : ['weight', 'throughput'];

    const validateGroup = (group) =>
      group.every((item) => {
        const { validateValue } = ValidateProductValue(
          item,
          selectedPdp,
          product[item],
          result,
          null,
          product,
          svFamily
        );
        return validateValue;
      });

    const sizeValid = validateGroup(sizeToCheck);
    const flowValid = validateGroup(flowToCheck);

    return {
      size_input: sizePresent && sizeValid,
      flow_input: flowPresent && flowValid,
      size_checked: null,
      size_warnings: null,
      flow_checked: null,
      flow_warnings: null,
    };
  });

  let readyIdx = -1;

  for (let i = 0; i < product_tbh.length; i++) {
    const product = product_tbh[i];
    const state = baseProductState[i];

    if (!checkProductReady(product)) {
      continue;
    }

    readyIdx += 1;

    const idxFlow = readyIdx;
    const idxClr = readyIdx + 1;

    let sizeChecked = true;
    let sizeWarning = null;

    const sizeChecks = [
      [
        result.le_horizontal_clearance?.[idxClr],
        'Horizontal clearance in lower end is too small.',
      ],
      [
        result.windings_horizontal_clearance?.[idxClr],
        'Horizontal clearance in windings is too small.',
      ],
      [
        result.ue_horizontal_clearance?.[idxClr],
        'Horizontal clearance in upper end is too small.',
      ],
      [
        result.le_vertical_clearance?.[idxClr],
        'Vertical clearance in lower end is too small.',
      ],
      [
        result.pitch_clearance?.[idxClr],
        'Vertical clearance in windings is too small.',
      ],
      [
        result.ue_vertical_clearance?.[idxClr],
        'Vertical clearance in upper end is too small.',
      ],
      [
        result.motor_clearance?.[idxClr],
        'Clearance under the drive is too small.',
      ],
    ];

    for (const [value, message] of sizeChecks) {
      if (tooSmall(value)) {
        sizeChecked = false;
        sizeWarning = message;
        break;
      }
    }

    let flowChecked = true;
    let flowWarning = null;

    const filling = result.filling_rate?.[idxFlow];
    const critCenter = result.critical_filling_rate_center?.[idxFlow];
    const critOutside = result.critical_filling_rate_outside?.[idxFlow];

    const criticals = [critCenter, critOutside].filter(isNum);
    const minCritical = criticals.length ? Math.min(...criticals) : undefined;

    if (isNum(filling) && isNum(minCritical) && filling > minCritical) {
      flowChecked = false;
      flowWarning = 'Filling rate is not supported.';
    }

    const load = result.all_product_load?.[idxFlow];
    const maxAllowed = result.max_allowed_load_line;

    if (
      svFamily !== 'SV-L' &&
      isNum(load) &&
      isNum(maxAllowed) &&
      load > maxAllowed
    ) {
      flowChecked = false;
      flowWarning = flowWarning ?? 'Product weight is not supported.';
    }

    baseProductState[i] = {
      ...state,
      size_checked: sizeChecked,
      size_warnings: sizeWarning,
      flow_checked: flowChecked,
      flow_warnings: flowWarning,
    };
  }
  return baseProductState;
}

export function updateProductMenuWarning(
  productState,
  inputs,
  options,
  result
) {
  const hasProductWarning = productState?.some(
    (p) =>
      p.size_input === false ||
      p.flow_input === false ||
      p.size_checked === false ||
      p.flow_checked === false
  );

  const pdpOption = options.find((option) => option.field === 'number_pdp');

  const validateResult = pdpOption
    ? ValidateValue(pdpOption, inputs?.number_pdp, inputs, result)
    : { validateValue: true };

  const hasPdpWarning = !validateResult.validateValue;

  return { pdp: hasPdpWarning, product: hasProductWarning };
}

export function productSizeCheck(isProduct, result) {
  if (!isProduct) return true;
  return (
    result?.product_max_length > 0 &&
    result?.horizontal_clearance > 0 &&
    result?.product_vertical_clearance > 0
  );
}

export function renderProductCheck(isProduct, result, unitSystem = 'metric') {
  const round0 = (v) => (typeof v === 'number' ? Math.round(v) : v);

  const items = [
    {
      label: 'Width margin',
      key: 'horizontal_clearance',
    },
    {
      label: 'Height margin',
      key: 'product_vertical_clearance',
    },
  ];

  return (
    <div className="result-product-container">
      {items.map(({ label, key }, i) => {
        const rawValue = result?.[key];

        const { displayedValue, displayedUnit } = UnitConversion(
          rawValue,
          unitSystem,
          'mm'
        );

        const roundedValue = round0(displayedValue);
        const isChecked = typeof rawValue === 'number' && rawValue > 0;

        return (
          <div
            key={i}
            className={`result-product ${!isChecked ? 'not-checked' : ''}`}
          >
            <div className="result-product-name">
              {isProduct ? label : label.replace(' margin', '')}
            </div>

            <div className="result-product-value">
              {roundedValue} {displayedUnit}
            </div>
          </div>
        );
      })}
    </div>
  );
}
